<template>
  <div>
    <div class="video-btn-1" v-if="isLocal">
      <el-tooltip
        :disabled="!hasMicError || isMobileDevice"
        :content="$t('deviceErrorGuide.tooltip')"
        placement="top"
      >
        <el-button
          @click.prevent="toggleMic"
          :class="{
            'device-mute': !hasMicError && !micEnabled,
            'device-error': hasMicError
          }"
          circle
        >
          <img
            v-if="!micEnabled"
            :src="require(`@/assets/img/mic_icn-off@2x.png`)"
            class="device-icon"
          />
          <MicVolumeLevel v-show="micEnabled" :key="micComponentKey" />
          <img :src="ExcalamationSvg" class="device-error-exclamation" v-if="hasMicError" />
        </el-button>
      </el-tooltip>
      <el-tooltip
        :disabled="!hasCameraError || isMobileDevice"
        :content="$t('deviceErrorGuide.tooltip')"
        placement="top"
      >
        <el-button
          @click.prevent="toggleCamera"
          :class="{
            'device-mute': !hasCameraError && !cameraEnabled,
            'device-error': hasCameraError
          }"
          circle
        >
          <img :src="cameraIconSource(cameraEnabled)" class="device-icon" />
          <img :src="ExcalamationSvg" class="device-error-exclamation" v-if="hasCameraError" />
        </el-button>
      </el-tooltip>
    </div>
    <div class="video-btn-1" v-else>
      <el-button
        v-if="!remoteMicEnabled"
        :class="['remote', { 'device-mute': !remoteMicEnabled }]"
        circle
      >
        <img :src="micIconSource(remoteMicEnabled)" class="device-icon" />
      </el-button>
    </div>

    <!-- Device Error Dialog Guide-->
    <DeviceErrorDialog
      v-if="displayDialog"
      :media-device="mediaDevice"
      @close-dialog="displayDialog = false"
    />
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import { store } from '@/utils/common';

import MicVolumeLevel from '@/components/Lesson/VideoChat/MicVolumeLevel';
import DeviceErrorDialog from '@/components/Lesson/VideoChat/DeviceErrorDialog';
import ExcalamationSvg from '@/assets/img/icon/device_error_exclamation.svg';

export default {
  components: { MicVolumeLevel, DeviceErrorDialog },
  data() {
    return {
      micComponentKey: 0,
      ExcalamationSvg: ExcalamationSvg,
      displayDialog: false,
      mediaDevice: ''
    };
  },
  props: ['isLocal', 'rtcClient'],
  watch: {
    cameraEnabled(state) {
      this.sendSignal('camera', state);
    },
    micEnabled(state) {
      this.sendSignal('mic', state);
    },
    streamHasChanged: {
      async handler() {
        // force render mic volume level component
        this.micComponentKey++;
      },
      deep: true
    },
    hasCameraError(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.setCameraEnabled(false);
        store.setCameraState(false);
        return;
      }
      this.setCameraEnabled(!newVal);
      store.setCameraState(!newVal);
    },
    hasMicError(state) {
      this.setMicEnabled(!state);
      store.setMicState(!state);
    }
  },
  computed: {
    ...mapState('socketio', ['socket', 'remoteMicEnabled', 'remoteCameraEnabled']),
    ...mapState('devices', [
      'cameraEnabled',
      'micEnabled',
      'cameras',
      'mics',
      'selectedCamera',
      'cameraDeviceError',
      'micDeviceError'
    ]),
    ...mapState(['userId', 'lessonId', 'lessonEnd']),
    ...mapState('videoRtc', ['localUser', 'activeAudioTrack']),
    ...mapGetters('devices', ['hasCameraError', 'hasMicError']),
    ...mapGetters('uaParser', ['isMobileDevice']),

    streamHasChanged() {
      return this.localUser.stream && this.activeAudioTrack;
    },
    cameraIconSource() {
      return function (cameraEnabled) {
        const iconType = cameraEnabled ? 'on' : 'off';
        return require(`@/assets/img/camera_icn-${iconType}@2x.png`);
      };
    },
    hasSelectedCamera() {
      return this.selectedCamera && this.selectedCamera != 'Unable to access camera';
    }
  },
  mounted() {
    if (this.isLocal && parseInt(store.get('lesson_id')) === parseInt(this.lessonId)) {
      this.initialize();
    }
  },
  methods: {
    ...mapMutations('devices', ['setCameraEnabled', 'setMicEnabled']),

    initialize() {
      const micToggled = store.getMicState(this.micEnabled);
      const cameraToggled = store.getCameraState(this.cameraEnabled);

      this.setMicEnabled(micToggled);
      this.setCameraEnabled(cameraToggled);
    },

    toggleMic() {
      if (this.hasMicError) {
        this.mediaDevice = 'mic';
        this.displayDialog = true;
        return;
      }
      this.setMicEnabled(!this.micEnabled);
      this.rtcClient.toggleMic(this.micEnabled);
      store.setMicState(this.micEnabled);
    },

    toggleCamera() {
      if (this.hasCameraError) {
        this.mediaDevice = 'camera';
        this.displayDialog = true;
        return;
      }
      this.setCameraEnabled(!this.cameraEnabled);
      this.rtcClient.toggleCamera(this.cameraEnabled);
      store.setCameraState(this.cameraEnabled);
    },

    sendSignal(device, toggled) {
      if (this.isLocal) {
        this.socket.emit('signal', { type: device, state: toggled });

        const state = toggled ? 'on' : 'off';
        this.$logger.info(`Turn ${state} ${device}`, { operation: `devices:${device}-${state}` });
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/video_buttons.css"></style>
<style scoped src="@/assets/css/sp/video_buttons.css"></style>
